
import { useNavigate, Link } from 'react-router-dom';
import Button from '../../components/base/Button';
import TestimonialsCarousel from '../../components/feature/TestimonialsCarousel';
import InstallAppCTA from '../../components/base/InstallAppCTA';
import OpenInBrowserButton from '../../components/feature/OpenInBrowserButton';
import { SEOHead } from '../../components/seo/SEOHead';
import { StructuredData } from '../../components/seo/StructuredData';
import { 
  medicalWebPageSchema, 
  softwareApplicationSchema, 
  organizationSchema,
  faqPageSchema 
} from '../../data/seoSchemas';
import { useWebViewDetection } from '../../hooks/useWebViewDetection';
import { getFeatureFlags } from '../../utils/environmentConfig';

export default function WelcomePage() {
  const navigate = useNavigate();
  const { isStandalone } = useWebViewDetection();
  const featureFlags = getFeatureFlags();

  const shouldHideLogin = featureFlags.requirePWAForLogin && !isStandalone;

  const handleLogin = () => {
    navigate('/login');
  };

  const handleSignUp = () => {
    navigate('/onboarding');
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHead
        title="Magistral Homeopatia - Sua Homeopatia na Hora Certa"
        description="Primeiro aplicativo completo para pacientes de homeopatia no Brasil. Gestão inteligente de tratamentos familiares e individuais com lembretes, biblioteca de remédios, consultas com homeopata virtual e gamificação. Grátis para começar."
        keywords="app homeopatia, pacientes homeopatia, lembrete medicação homeopática, tratamento homeopático família, medicina integrativa"
      />
      
      {/* Structured Data para Rich Results */}
      <StructuredData data={medicalWebPageSchema} />
      <StructuredData data={softwareApplicationSchema} />
      <StructuredData data={organizationSchema} />
      <StructuredData data={faqPageSchema} />
      
      <div 
        className="min-h-screen flex flex-col items-center justify-center px-6 py-8 relative"
        style={{
          backgroundImage: 'url(/Imagens/natilus%20p%C3%A1gina%20principal.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
      {/* Overlay para melhorar legibilidade do texto */}
      <div className="absolute inset-0 bg-white/10 backdrop-blur-[0.5px]"></div>
      
      {/* Conteúdo da página */}
      <div className="relative z-10 flex flex-col items-center justify-center w-full">
        {/* Logo e Header Principal */}
        <div className="text-center mb-12">
          <div className="w-32 h-32 rounded-3xl flex items-center justify-center mb-6 mx-auto shadow-card overflow-hidden bg-white/20 backdrop-blur-sm">
            <img
              src="/Imagens/logo magistral 2.0 verde.png"
              alt="Logotipo do Magistral"
              className="w-full h-full object-cover object-center"
            />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4 font-sans" style={{ 
            fontWeight: 700,
            textShadow: '2px 4px 8px rgba(0, 0, 0, 0.6), 0px 2px 4px rgba(0, 0, 0, 0.3)'
          }}>
            Magistral
          </h1>
          <p className="text-white text-lg leading-relaxed max-w-sm mx-auto font-sans" style={{ fontWeight: 500, textShadow: '2px 2px 4px rgba(0,0,0,0.5)' }}>
            Viva com mais leveza cuidando da saúde da sua família com Homeopatia
          </p>
        </div>

        {/* Espaço onde estava a imagem da família - removido */}
        <div className="mb-12 w-full max-w-xs">
          {/* Imagem removida conforme solicitado */}
        </div>

        {/* Benefícios Principais */}
        <div className="grid grid-cols-1 gap-4 mb-12 w-full max-w-sm">
          <div className="bg-white/90 backdrop-blur-sm rounded-card p-4 shadow-card flex items-center border border-white/20">
            <div className="w-12 h-12 bg-green-100 rounded-card flex items-center justify-center mr-4 overflow-hidden">
              <img 
                src="/Imagens/familia-estilizada.png" 
                alt="Família estilizada" 
                className="w-8 h-8 object-contain"
              />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 text-sm font-sans" style={{ fontWeight: 700 }}>Para toda família</h3>
              <p className="text-gray-600 text-xs font-sans" style={{ fontWeight: 500 }}>Gerencie o tratamento de todos</p>
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-card p-4 shadow-card flex items-center border border-white/20">
            <div className="w-12 h-12 bg-primary-100 rounded-card flex items-center justify-center mr-4">
              <i className="ri-notification-3-line text-primary-500 text-xl"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 text-sm font-sans" style={{ fontWeight: 700 }}>Lembretes inteligentes</h3>
              <p className="text-gray-600 text-xs font-sans" style={{ fontWeight: 500 }}>Nunca esqueça uma dose</p>
            </div>
          </div>

          <div className="bg-white/90 backdrop-blur-sm rounded-card p-4 shadow-card flex items-center border border-white/20">
            <div className="w-12 h-12 bg-orange-100 rounded-card flex items-center justify-center mr-4">
              <i className="ri-trophy-line text-orange-500 text-xl"></i>
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 text-sm font-sans" style={{ fontWeight: 700 }}>Sistema de pontos</h3>
              <p className="text-gray-600 text-xs font-sans" style={{ fontWeight: 500 }}>Motivação para toda família</p>
            </div>
          </div>
        </div>

        {/* Botões de Ação */}
        <div className="w-full max-w-sm space-y-4">
          <Button 
            onClick={handleSignUp}
            size="lg" 
            className="w-full"
          >
            <i className="ri-user-add-line mr-2 text-xl"></i>
            Criar Conta
          </Button>

          {!shouldHideLogin && (
            <Button 
              onClick={handleLogin}
              variant="secondary"
              size="lg" 
              className="w-full bg-white/90 backdrop-blur-sm hover:bg-white/95"
            >
              <i className="ri-login-box-line mr-2 text-xl"></i>
              Já tenho conta
            </Button>
          )}
        </div>

        {/* Informações de Segurança */}
        <div className="flex items-center justify-center mt-8 space-x-6">
          <div className="text-center">
            <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-1 mx-auto border border-white/30">
              <i className="ri-shield-check-line text-white text-sm"></i>
            </div>
            <p className="text-xs text-white font-sans drop-shadow-sm" style={{ fontWeight: 500 }}>Seguro</p>
          </div>
          <div className="text-center">
            <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-1 mx-auto border border-white/30">
              <i className="ri-lock-line text-white text-sm"></i>
            </div>
            <p className="text-xs text-white font-sans drop-shadow-sm" style={{ fontWeight: 500 }}>Privado</p>
          </div>
          <div className="text-center">
            <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-1 mx-auto border border-white/30">
              <i className="ri-heart-line text-white text-sm"></i>
            </div>
            <p className="text-xs text-white font-sans drop-shadow-sm" style={{ fontWeight: 500 }}>Saúde</p>
          </div>
        </div>
        {/* CTA de Instalação do App */}
        <div className="w-full max-w-lg mt-6">
          <InstallAppCTA />
        </div>

        {/* Botão para abrir em navegador principal */}
        <div className="w-full max-w-lg mt-4">
          <OpenInBrowserButton variant="secondary" />
        </div>

        {/* Seção de Depoimentos */}
        <div className="w-full max-w-lg mt-12 mb-8">
          <div className="text-center mb-6">
            <h2 className="text-white text-xl font-bold mb-2 font-sans" style={{ 
              fontWeight: 700,
              textShadow: '2px 4px 8px rgba(0, 0, 0, 0.6), 0px 2px 4px rgba(0, 0, 0, 0.3)'
            }}>
              O que nossos usuários dizem
            </h2>
            <p className="text-white/90 text-sm font-sans" style={{ 
              fontWeight: 500, 
              textShadow: '1px 2px 4px rgba(0,0,0,0.5)' 
            }}>
              Histórias reais de famílias que transformaram sua saúde
            </p>
          </div>
          <TestimonialsCarousel />
        </div>

        {/* Mensagem Motivacional */}
        <div className="text-center mt-8">
          <p className="text-white text-sm font-sans drop-shadow-sm" style={{ fontWeight: 500 }}>
            ✨ Seja sua melhor versão • Versão 1.0
          </p>
        </div>

        {/* Link visível para Política de Privacidade e Termos de Uso (requisito do Google OAuth) */}
        <div className="text-center mt-4">
          <Link
            to="/terms-and-privacy"
            className="text-white underline hover:text-white/90 text-xs font-sans"
            style={{ textShadow: '1px 2px 4px rgba(0,0,0,0.6)' }}
            aria-label="Política de Privacidade e Termos de Uso"
          >
            Política de Privacidade e Termos de Uso
          </Link>
        </div>
      </div>
    </div>
    </>
  );
}
